import React, { useState, useRef } from 'react';
import { Grid, Box, Typography, Tooltip, IconButton, SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import TranslateIcon from '@mui/icons-material/Translate';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SpellcheckEditor from './SpellcheckEditor';
import TagTextField from './TagTextField';

const TranslationRow = ({
    globalIndex,
    originalText,
    translatedText,
    isRepeat,
    tagMap,
    isProjectOwner,
    sourceDirection,
    targetDirection,
    grammarErrors,
    hasNote,
    isApproved,
    confidenceScore,
    translationSource,
    onOriginalTextChange,
    onTranslationChange,
    onGrammarReplace, // Used by SpellcheckEditor
    onGrammarClick,   // Used by non-editable view
    highlightGrammarIssues, // Helper function passed down
    onToggleApproval,
    onTranslate,
    onAddAnnotation,
    onCopyToClipboard,
    onAddGlossaryTerm
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [speedDialDirection, setSpeedDialDirection] = useState('down');
    const rowRef = useRef(null);

    const determineSpeedDialDirection = () => {
        const rect = rowRef.current?.getBoundingClientRect();
        if (rect) {
            setSpeedDialDirection(rect.bottom > window.innerHeight - 200 ? 'up' : 'down');
        }
    };

    return (
        <Grid item xs={12}>
            <Box
                ref={rowRef}
                onMouseEnter={() => {
                    setIsHovered(true);
                    determineSpeedDialDirection();
                }}
                onMouseLeave={() => setIsHovered(false)}
                sx={{ position: 'relative', py: 1 }}
            >
                <Grid container spacing={2} alignItems="flex-start">
                    {/* Segment Number + RP */}
                    <Grid item sx={{ width: 60, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isRepeat && (
                            <Tooltip title="Repeated Section">
                                <Box component="span" sx={{
                                    background: '#e1e7f5',
                                    color: '#234fd2',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    px: 1, mr: 1,
                                    border: '1px solid #bfc9e8'
                                }}>
                                    RP
                                </Box>
                            </Tooltip>
                        )}
                        <Typography variant="body2">{globalIndex + 1}</Typography>
                        {hasNote && (
                            <Tooltip title="This section has a note">
                                <IconButton size="small" color="secondary" sx={{ position: 'absolute', bottom: 0, right: -20 }}>
                                    <NoteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>

                    {/* Original Text */}
                    <Grid item xs>
                        {isProjectOwner ? (
                            <SpellcheckEditor
                                dir={sourceDirection}
                                tagMap={tagMap}
                                style={{
                                    minHeight: 56,
                                    border: '1px solid rgba(0,0,0,0.23)',
                                    borderRadius: 4,
                                    padding: '16.5px 14px',
                                    boxSizing: 'border-box',
                                    textAlign: sourceDirection === 'rtl' ? 'right' : 'left'
                                }}
                                value={originalText}
                                onChange={newText => onOriginalTextChange(globalIndex, newText)}
                                errors={grammarErrors || []}
                                onReplace={(sug, errIdx) => onGrammarReplace && onGrammarReplace(globalIndex, sug, errIdx)}
                                readOnly={false}
                            />
                        ) : (
                            <Box
                                sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: '16.5px 14px', minHeight: 56, whiteSpace: 'pre-wrap', textAlign: sourceDirection === 'rtl' ? 'right' : 'left' }}
                                dir={sourceDirection}
                                dangerouslySetInnerHTML={{ __html: highlightGrammarIssues ? highlightGrammarIssues(globalIndex, originalText, grammarErrors || []) : originalText }}
                                onClick={onGrammarClick}
                            />
                        )}
                    </Grid>

                    {/* Translated Text */}
                    <Grid item xs>
                        <TagTextField
                            value={translatedText || ''}
                            tagMap={tagMap}
                            onChange={v => onTranslationChange(globalIndex, v)}
                            dir={targetDirection}
                            style={{
                                textAlign: targetDirection === 'rtl' ? 'right' : 'left',
                                backgroundColor: isApproved ? '#f0f0f0' : 'white',
                                minHeight: 56,
                                border: '1px solid rgba(0,0,0,0.23)',
                                borderRadius: 4,
                                padding: '16.5px 14px',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                            disabled={isApproved}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                            {translationSource
                                ? `${translationSource === 'memory' ? 'Memory' : 'AI'} (${Math.round(confidenceScore || 0)}%)`
                                : `Confidence: ${Math.round(confidenceScore || 0)}%`}
                        </Typography>
                    </Grid>

                    {/* Actions */}
                    <Grid item sx={{ width: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {isHovered && (
                            <SpeedDial
                                ariaLabel="Actions"
                                icon={<SpeedDialIcon />}
                                direction={speedDialDirection}
                                sx={{
                                    position: 'absolute',
                                    top: speedDialDirection === 'down' ? 0 : 'auto',
                                    bottom: speedDialDirection === 'up' ? 0 : 'auto',
                                    right: 0,
                                    zIndex: 1500
                                }}
                            >
                                <SpeedDialAction
                                    icon={isApproved ? <UndoIcon /> : <CheckCircleIcon />}
                                    tooltipTitle={isApproved ? 'Unapprove' : 'Approve'}
                                    onClick={() => onToggleApproval(globalIndex)}
                                />
                                {isProjectOwner && (
                                    <SpeedDialAction
                                        icon={<TranslateIcon />}
                                        tooltipTitle="Translate"
                                        onClick={() => onTranslate(globalIndex)}
                                    />
                                )}
                                <SpeedDialAction
                                    icon={<AddCommentIcon />}
                                    tooltipTitle="Add Annotation"
                                    onClick={() => onAddAnnotation(globalIndex)}
                                />
                                <SpeedDialAction
                                    icon={<ContentCopyIcon />}
                                    tooltipTitle="Copy"
                                    onClick={() => onCopyToClipboard(translatedText)}
                                />
                                <SpeedDialAction
                                    icon={<LibraryBooksIcon />}
                                    tooltipTitle="Glossary Term"
                                    onClick={() => onAddGlossaryTerm(globalIndex)}
                                />
                            </SpeedDial>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </Grid>
    );
};

export default TranslationRow;
